import { extractApiErrorMessage } from '@/utils/dataNormalization';

export const resolveDocumentDownloadFileName = (response, fallbackName = '') => {
  const disposition = String(response?.headers?.['content-disposition'] || '').trim();
  const match = disposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
  const parsedName = match && match[1]
    ? decodeURIComponent(String(match[1]).replace(/\"/g, '').trim())
    : '';

  return parsedName || fallbackName;
};

export const normalizeDocumentDownloadNameSegment = (value, fallbackSegment = '') => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || fallbackSegment;
};

export const buildDocumentDownloadTimestamp = (date = new Date()) => {
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

export const resolveDocumentDownloadErrorMessage = async (error, fallbackMessage = '') => {
  const blobData = error?.response?.data;

  if (typeof Blob !== 'undefined' && blobData instanceof Blob) {
    try {
      const text = await blobData.text();
      if (text) {
        const parsed = JSON.parse(text);
        const parsedMessage = String(parsed?.message || '').trim();
        if (parsedMessage !== '') {
          return parsedMessage;
        }
      }
    } catch (parseError) {
      // Fallback to shared parser.
    }
  }

  return extractApiErrorMessage(error, fallbackMessage) || fallbackMessage;
};

export const resolveDocumentUrl = (row = {}) => (
  String(row?.documentUrl || row?.document_url || '').trim()
);

const MIME_QUERY_HINTS = [
  'response-content-type',
  'content_type',
  'contentType',
  'mime',
  'mime_type',
  'type',
];

const resolveMimeHintFromUrl = (value = '') => {
  const rawValue = String(value || '').trim();
  if (rawValue === '') {
    return '';
  }

  try {
    const url = new URL(rawValue, window.location.origin);
    for (let index = 0; index < MIME_QUERY_HINTS.length; index += 1) {
      const key = MIME_QUERY_HINTS[index];
      const candidate = String(url.searchParams.get(key) || '').trim().toLowerCase();
      if (candidate !== '') {
        return candidate;
      }
    }
  } catch (error) {
    // Ignore invalid URL values.
  }

  return '';
};

const extractFileExtension = (value = '') => {
  const normalized = String(value || '').trim();
  if (normalized === '') {
    return '';
  }

  try {
    const withoutQuery = normalized.replace(/\\/g, '/').split('?')[0].split('#')[0];
    const fileName = decodeURIComponent(String(withoutQuery.split('/').pop() || '').trim());
    const match = fileName.match(/\.([a-z0-9]{2,8})$/i);
    return match ? String(match[1] || '').trim().toLowerCase() : '';
  } catch (error) {
    return '';
  }
};

export const isPdfDocument = (row = {}) => {
  const directNameCandidates = [
    String(row?.name || '').trim(),
    String(row?.fileName || '').trim(),
  ];

  const urlCandidates = [
    String(row?.documentRaw || row?.document || '').trim(),
    resolveDocumentUrl(row),
  ];

  const directExtension = directNameCandidates
    .map((candidate) => extractFileExtension(candidate))
    .find((extension) => extension !== '');

  if (directExtension !== '') {
    return directExtension === 'pdf';
  }

  const urlExtension = urlCandidates
    .map((candidate) => extractFileExtension(candidate))
    .find((extension) => extension !== '');

  if (urlExtension !== '') {
    return urlExtension === 'pdf';
  }

  const mimeCandidates = [
    String(row?.content_type || row?.contentType || row?.mime_type || row?.mimeType || '').trim().toLowerCase(),
    ...urlCandidates.map((candidate) => resolveMimeHintFromUrl(candidate)),
  ];

  const mimeHint = mimeCandidates.find((candidate) => candidate !== '');
  if (mimeHint !== undefined) {
    return mimeHint.includes('application/pdf');
  }

  return false;
};

export const resolveDocumentFileName = (row = {}, fallbackName = 'documento') => {
  const candidates = [
    String(row?.name || '').trim(),
    String(row?.fileName || '').trim(),
  ];

  const namedCandidate = candidates.find((value) => value !== '' && /\.[a-z0-9]{2,8}$/i.test(value));
  if (namedCandidate) {
    return namedCandidate;
  }

  const source = String(row?.documentRaw || resolveDocumentUrl(row) || '').trim();
  if (source !== '') {
    try {
      const normalized = source.replace(/\\/g, '/');
      const withoutQuery = normalized.split('?')[0].split('#')[0];
      const parsedName = decodeURIComponent(String(withoutQuery.split('/').pop() || '').trim());

      if (parsedName !== '') {
        return parsedName;
      }
    } catch (error) {
      // Ignore parse errors and keep fallback.
    }
  }

  return String(fallbackName || 'documento').trim() || 'documento';
};

export const openDocumentInNewTab = (url) => {
  const href = String(url || '').trim();
  if (href === '') {
    return false;
  }

  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();

  return true;
};

export const triggerDocumentDirectDownload = async (url, fileName = 'documento') => {
  const href = String(url || '').trim();
  if (href === '') {
    return false;
  }

  const safeFileName = String(fileName || 'documento').trim() || 'documento';

  try {
    const response = await fetch(href, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Download request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);

    const downloadLink = document.createElement('a');
    downloadLink.href = objectUrl;
    downloadLink.setAttribute('download', safeFileName);
    downloadLink.rel = 'noopener noreferrer';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();

    window.URL.revokeObjectURL(objectUrl);
    return true;
  } catch (error) {
    // Fallback: still try native download hint in a separate tab context.
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.setAttribute('download', safeFileName);
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();

  return true;
  }
};
